import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView, sanitizePath } from '@/lib/analytics';
import { useCookieConsent } from '@/hooks/useCookieConsent';
import { usePlatformSettingsContext } from '@/contexts/PlatformSettingsContext';

/**
 * Hook to automatically track page views on route changes.
 * Uses a small delay to ensure document.title is updated.
 * 
 * IMPORTANT: Only use this in PublicShell for marketing pages.
 * Dashboard/authenticated pages should NOT be tracked.
 * 
 * Note: Initial page view is handled by PublicShell after GA loads.
 * This hook only tracks subsequent navigation.
 */
export function usePageTracking() {
  const location = useLocation();
  const { analyticsEnabled } = useCookieConsent();
  const { isPublicFlagsLoading } = usePlatformSettingsContext();
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Skip if flags are still loading to prevent tracking before consent state is known
    if (isPublicFlagsLoading) return;
    
    // Skip if analytics not enabled (no consent)
    if (!analyticsEnabled) return;
    
    // Skip first render - initial page view is handled by PublicShell
    // after GA script is fully loaded to avoid race condition
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Small delay to ensure document.title is updated by Helmet
    const timeoutId = setTimeout(() => {
      const safePath = sanitizePath(location.pathname + location.search);
      trackPageView(safePath);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [location.pathname, location.search, analyticsEnabled, isPublicFlagsLoading]);
}
